import { Icon } from '../../ui/Icon.js';

/**
 * Persona-lens-in-action: one AI answer, scored two different ways.
 *
 * The SAME answer snippet is shown once in the middle; two persona cards each
 * declare the real GEO Observatory dimensions (priority · wellbeing · anti-signal) and
 * then land on opposite verdicts — *with the reason*, which is the teaching
 * moment. Hand-built HTML/CSS, no data, no images. Pills reuse the house
 * green/amber "fit" styling used elsewhere on the page.
 */

interface PersonaDimension {
  readonly label: string;
  readonly value: string;
}

interface Persona {
  readonly name: string;
  readonly dims: readonly PersonaDimension[];
  readonly verdict: 'strong fit' | 'weak fit';
  readonly reason: string;
}

const PERSONAS: readonly Persona[] = [
  {
    name: 'Budget-first parent',
    dims: [
      { label: 'Priority', value: 'lowest total cost' },
      { label: 'Wellbeing', value: 'no stressful surprises' },
      { label: 'Anti-signal', value: 'hidden fees' },
    ],
    verdict: 'strong fit',
    reason: 'names the price up front — no surprises',
  },
  {
    name: 'Performance athlete',
    dims: [
      { label: 'Priority', value: 'proven results & data' },
      { label: 'Wellbeing', value: 'confidence in claims' },
      { label: 'Anti-signal', value: 'vague marketing' },
    ],
    verdict: 'weak fit',
    reason: 'no performance data, vague claims',
  },
] as const;

/** Green/amber "fit" pill — matches the house badge styling on this page. */
function FitPill({ verdict }: { verdict: Persona['verdict'] }) {
  const isStrong = verdict === 'strong fit';
  return (
    // Spec 250: a verdict is a status, so it is Kridt's chip with a tone.
    <span className={`hub-chip ${isStrong ? 'hub-chip--ok' : 'hub-chip--warn'} shrink-0`}>
      {isStrong ? <Icon name="bx-check" /> : <Icon name="bx-error" />}
      {verdict}
    </span>
  );
}

export function PersonaLensDiagram() {
  return (
    <div data-testid="hiw-persona-cards" className="space-y-4">
      {/* The shared answer — shown once, scored twice. */}
      <figure className="overflow-hidden rounded-xl border border-[var(--hub-color-line)] bg-[var(--hub-color-surface)]">
        <figcaption className="flex items-center justify-between border-b border-[var(--hub-color-line)] bg-[var(--hub-color-surface-sunken)] px-4 py-2">
          <span className="text-xs font-semibold text-[var(--hub-color-ink-muted)]">
            One AI answer
          </span>
          <span className="hub-tag">same content</span>
        </figcaption>
        <blockquote className="px-4 py-3 text-[13px] leading-relaxed text-[var(--hub-color-ink)]">
          “The TrailGrip 2 is a popular everyday running shoe. It’s comfortable,
          stylish, and trusted by thousands of runners — a solid all-rounder for
          anyone getting into the sport.”
        </blockquote>
      </figure>

      {/* Same answer, opposite verdicts. */}
      <div className="grid gap-4 sm:grid-cols-2">
        {PERSONAS.map((p) => (
          <div
            key={p.name}
            className="flex flex-col overflow-hidden rounded-xl border border-[var(--hub-color-line)] bg-[var(--hub-color-surface)]"
          >
            <div className="border-b border-[var(--hub-color-line)] px-4 py-2.5">
              <span className="text-sm font-semibold text-[var(--hub-color-ink)]">
                {p.name}
              </span>
            </div>

            {/* Persona dimensions — labelled rows. */}
            <dl className="divide-y divide-[var(--hub-color-line)]">
              {p.dims.map((d) => (
                <div key={d.label} className="flex items-baseline gap-3 px-4 py-2">
                  <dt className="w-24 shrink-0 text-xs font-medium text-[var(--hub-color-ink-muted)]">
                    {d.label}
                  </dt>
                  <dd className="text-[13px] text-[var(--hub-color-ink)]">{d.value}</dd>
                </div>
              ))}
            </dl>

            {/* Scored verdict WITH the reason — the teaching moment. */}
            <div className="mt-auto flex items-start gap-2.5 border-t border-[var(--hub-color-line)] bg-[var(--hub-color-surface-sunken)] px-4 py-3">
              <FitPill verdict={p.verdict} />
              <p className="text-xs leading-relaxed text-[var(--hub-color-ink-muted)]">
                {p.reason}
              </p>
            </div>
          </div>
        ))}
      </div>

      <p className="text-center text-xs text-[var(--hub-color-ink-subtle)]">
        Same answer, opposite verdicts — the persona’s lens decides the fit.
      </p>
    </div>
  );
}
